import { FlakinessReport } from '@flakiness/flakiness-report';
import { Query } from '@flakiness/server/common/fql/query.js';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { TimePeriod } from '@flakiness/server/common/timePeriod.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlRadioGroup } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { Temporal } from 'temporal-polyfill';
import { api } from '../api.js';
import { DataTableProvider } from '../components/data-table.js';
import { FKPeriodSelectEvent } from '../components/period-picker.js';
import { FqlChangedEvent } from '../components/fql-search.js';
import { FKHeadSelectEvent } from '../components/head-selector.js';
import { FKOutcomeSelectEvent, OUTCOME_LABELS, OUTCOME_ORDER, outcomeSwatchFill } from '../components/outcome-legend.js';
import { PeriodChartState, rangeLabel } from '../components/period-chart.js';
import { consume, contexts, ContextTask } from '../contexts.js';
import { createTestHistoryColumn } from '../data-table-providers/genericColumns.js';
import { createStatusColumn, testStatsColumns } from '../data-table-providers/testStatsProvider.js';
import { handleLink, Linkifier } from '../linkifier.js';
import { PageRun } from '../page-run.js';
import { tasks } from '../tasks.js';
import { FeatureCommitHistoryPopup } from './feature-commits-history.js';

/**
 * What the page shows. An optional field follows the live value instead of pinning one: the
 * newest day, the default period, every test. A required field always carries a concrete value,
 * which the URL fills in from its default.
 */
export type FeatureProjectAnalyticsState = {
  timelineSplit?: EnvGroup,
  head?: string,
  period: AnalyticsPeriod,
  classification: WireTypes.OutcomeClassification,
  // A day in the newest period the chart shows. The chart ends with that whole period, or
  // with today while the period is still running. Undefined follows today.
  until?: Temporal.PlainDate,
  // The first day of the period whose details are shown under the chart. Undefined follows the
  // default period.
  selected?: Temporal.PlainDate,
  // The transitions whose tests are listed under the period details, if any.
  transitions?: WireTypes.TransitionFilter[],
  // An FQL filter over the listed tests.
  query?: string,
}

/** The periods the chart can show. Three-hour spans only bucket the history of a test. */
export const PERIODS = ['day', 'usweek', 'month', 'quarter'] as const satisfies readonly TimePeriod.Period[];
export type AnalyticsPeriod = typeof PERIODS[number];

// What one bar covers, as the aggregation picker names it: "45 days", "20 weeks".
const PERIOD_UNITS: Record<AnalyticsPeriod, string> = {
  day: 'day',
  usweek: 'week',
  month: 'month',
  quarter: 'quarter',
};

// How a test's many results in a period collapse into one outcome.
const CLASSIFICATION_LABELS: Record<WireTypes.OutcomeClassification, string> = {
  worst: 'Worst result wins',
  eod: 'Last result wins',
};

// The help card's example: one test's results over a period's commits, oldest first,
// and the result each aggregation picks from them.
const AGGREGATION_EXAMPLE: WireTypes.Outcome[] = ['expected', 'unexpected', 'expected', 'expected', 'flaked'];
const AGGREGATION_EXAMPLE_WINNER: Record<WireTypes.OutcomeClassification, number> = {
  worst: 1,
  eod: 4,
};

// The outcome picker shows icons only: a skull takes the worst result, a finish flag takes the last one.
const CLASSIFICATION_ICONS: Record<WireTypes.OutcomeClassification, { library?: string, name?: string, src?: string }> = {
  worst: { src: '/skull-crossbones-solid.svg' },
  eod: { library: 'boxicons', name: 'bxs-flag-checkered' },
};

function classificationIcon(classification: WireTypes.OutcomeClassification) {
  const icon = CLASSIFICATION_ICONS[classification];
  return html`<sl-icon library=${icon.library ?? nothing} name=${icon.name ?? nothing} src=${icon.src ?? nothing} label=${CLASSIFICATION_LABELS[classification]}></sl-icon>`;
}

function renderAggregationExample(classification: WireTypes.OutcomeClassification) {
  const winner = AGGREGATION_EXAMPLE_WINNER[classification];
  const outcome = AGGREGATION_EXAMPLE[winner];
  return html`
    <div class=aggregation-example>
      <div class=aggregation-title>${classificationIcon(classification)} ${CLASSIFICATION_LABELS[classification]}</div>
      <h-box class=aggregation-flow>
        <h-box class=aggregation-bars>
          ${AGGREGATION_EXAMPLE.map((result, index) => html`
            <span class=aggregation-bar ?winner=${index === winner} style=${styleMap({ background: outcomeSwatchFill(result) })}></span>
          `)}
        </h-box>
        <sl-icon name=arrow-right></sl-icon>
        <h-box class=aggregation-outcome>
          <fk-outcome outcome=${outcome}></fk-outcome>
          ${OUTCOME_LABELS[outcome]}
        </h-box>
      </h-box>
    </div>
  `;
}

// The number of bars per granularity. The aggregation picker names a window by it.
const PERIOD_COUNTS: Record<AnalyticsPeriod, number> = {
  day: 45,
  usweek: 20,
  month: 12,
  quarter: 4,
};

const windowLabel = (period: AnalyticsPeriod) => `${PERIOD_COUNTS[period]} ${PERIOD_UNITS[period]}s`;

/**
 * The sparkline a listed test carries: bars one step finer than the selected period, about two
 * periods' worth of them, so a row shows how the test behaved before and during that period.
 */
const HISTORY_BUCKETS: Record<AnalyticsPeriod, { bucket: WireTypes.HistoryType, depth: number }> = {
  day: { bucket: '3h', depth: 16 },
  usweek: { bucket: 'day', depth: 14 },
  month: { bucket: 'monthweek', depth: 10 },
  quarter: { bucket: 'month', depth: 6 },
};

/**
 * The period this one was compared with. The server picks the most recent earlier period that
 * reported tests, so it is not always the one right before this one.
 */
function comparedTo(period: WireTypes.DayPeriodSummary): { since: Temporal.PlainDate, until: Temporal.PlainDate }|undefined {
  return period.comparedTo && {
    since: Temporal.PlainDate.from(period.comparedTo.since),
    until: Temporal.PlainDate.from(period.comparedTo.until),
  };
}

/**
 * The last day the chart shows: the end of the period that holds `until`, or today while
 * that period is still running.
 */
function windowEnd(period: AnalyticsPeriod, until: Temporal.PlainDate, today: Temporal.PlainDate): Temporal.PlainDate {
  const end = TimePeriod.lastDay(TimePeriod.periodEnd(period, until.toPlainDateTime()));
  return Temporal.PlainDate.compare(end, today) < 0 ? end : today;
}

/**
 * Boundaries of `count` periods, where the newest period contains `until` and ends at it.
 * All other periods are whole periods. The last boundary is exclusive.
 */
function periodBoundaries(period: AnalyticsPeriod, until: Temporal.PlainDate, count: number): Temporal.PlainDate[] {
  const boundaries = [until.add({ days: 1 })];
  for (let i = 0; i < count; ++i)
    boundaries.push(TimePeriod.periodStart(period, boundaries[i].subtract({ days: 1 }).toPlainDateTime()).toPlainDate());
  return boundaries.reverse();
}

// How an outcome reads in the title of the listed tests: "Was passing", "still failing".
const OUTCOME_STATES: Record<WireTypes.AnalyticsOutcome, string> = {
  expected: 'passing',
  unexpected: 'failing',
  flaked: 'flaky',
  skipped: 'skipped',
};

/**
 * A title for the list of tests a filter selects, e.g. "Now flaky", "Was passing, now flaky"
 * or "Was failing, still failing".
 */
function transitionsLabel(filters: WireTypes.TransitionFilter[]): string {
  const was = (outcome: WireTypes.AnalyticsOutcome|undefined) => outcome ? `Was ${OUTCOME_STATES[outcome]}` : 'Was not reported';
  const now = (outcome: WireTypes.AnalyticsOutcome|undefined) => outcome ? `Now ${OUTCOME_STATES[outcome]}` : 'No longer reported';
  // A whole row or column is spelled out as one filter per cell, but it reads as one thing:
  // every entry shares a side, and between them they cover the other side.
  if (filters.length > 1) {
    const froms = new Set(filters.map(filter => filter.from));
    const tos = new Set(filters.map(filter => filter.to));
    if (froms.size === 1 && tos.size === filters.length)
      return was(filters[0].from);
    if (tos.size === 1 && froms.size === filters.length)
      return now(filters[0].to);
  }
  return filters.map(({ from, to }) => {
    if (from && from === to)
      return `${was(from)}, still ${OUTCOME_STATES[to]}`;
    return `${was(from)}, ${now(to).toLowerCase()}`;
  }).join(' · ');
}

@customElement('feature-project-analytics')
class FeatureProjectAnalytics extends LitElement {

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _branches?: ContextType<typeof contexts.projectReferences>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.commitRegistry, subscribe: true }) private _commitRegistry?: ContextType<typeof contexts.commitRegistry>;

  @property({ attribute: false }) state?: FeatureProjectAnalyticsState;
  @property({ attribute: false }) linkifier?: Linkifier<FeatureProjectAnalyticsState>;

  @query('feature-commit-history-popup') private _historyPopup?: FeatureCommitHistoryPopup;

  // The listed rows highlight what the filter matched, and their file paths link to it.
  private _fqlTask = new ContextTask(this, contexts.fql, {
    args: () => [this.state?.query] as const,
    task: async ([query]) => Query.parse(query ?? ''),
  });

  private _fqlLinker = new ContextTask(this, contexts.linkFQL, {
    args: () => [this.linkifier] as const,
    task: async () => (query: string) => this._linkify({ query }),
  });

  // A listed row's environment picks that environment in the timeline selector.
  private _timelineLinker = new ContextTask(this, contexts.linkTimeline, {
    args: () => [this.linkifier] as const,
    task: async () => (timeline: EnvGroup) => this._linkify({ timelineSplit: timeline }),
  });

  /**
   * Outcomes stacked on the chart, local to the view like the history chart's. The chart opens
   * on the period totals, and picking outcomes in the legend breaks them down.
   */
  @state() private _selectedOutcomes: WireTypes.Outcome[] = [];

  // Rows link to the run that produced them, the same way the tests page links them.
  private _testLinkRenderer = new ContextTask(this, contexts.linkTest, {
    args: () => [this._project, this._snapshotId, this._split()] as const,
    task: async ([project, snapshotId, timelineSplit]) => {
      CHECK(project && timelineSplit);
      return (testStats: WireTypes.TestStats, revealId?: string) => PageRun.url({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        reportId: testStats.runId,
        timelineSplit,
        testId: testStats.test.testId,
        testTimeline: EnvGroup.deserialize(testStats.timeline),
        snapshotId,
      }) + (revealId ? '#' + revealId : '');
    },
  });

  private _headRefTask = tasks.headRef(this, {
    head: () => this.state?.head,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
    defaultBranch: () => this._branches?.defaultBranch,
  });

  // The feature picks its own head, so it owns the default split of that head's history.
  private _defaultSplit = tasks.defaultSplit(this, {
    preset: () => this._headRefTask.value ? { type: 'commit', commitId: this._headRefTask.value.commit.commitId } : undefined,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  private _split() {
    return this.state?.timelineSplit ?? this._defaultSplit.value;
  }

  // "All Environments" stays out of the state, so that it follows the default split of the head.
  // Once the default is known, a split equal to it is dropped, without a new history entry.
  private _canonicalLinkTask = new Task(this, {
    args: () => [this.state?.timelineSplit, this._defaultSplit.value] as const,
    task: async ([split, defaultSplit]) => {
      if (split && defaultSplit && split.isEqual(defaultSplit))
        handleLink(this._router, this._linkify({ timelineSplit: undefined }), { replace: true });
    },
  });

  private _periodsTask = new Task(this, {
    args: () => [
      this._project,
      this._snapshotId,
      this._headRefTask.value,
      this.state?.period,
      this.state?.classification,
      this.state?.until,
      this._split(),
    ] as const,
    task: async ([project, snapshotId, headRef, period, classification, until, split], { signal }) => {
      CHECK(project && snapshotId && headRef && period && classification && split);
      const today = Temporal.Now.plainDateISO(project.timezone);
      const periods = await api.analytics.periods.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        head: headRef.commit.commitId,
        timeZoneId: project.timezone,
        classification,
        // One extra period before the window, so that the oldest bar has a comparison too.
        boundaries: periodBoundaries(period, windowEnd(period, until ?? today, today), PERIOD_COUNTS[period] + 1).map(day => day.toString()),
        timelineSplit: split.serialize(),
      }, { signal });
      return periods.slice(1);
    },
  });

  // The selected period, if it is part of the window the chart shows, or the default one.
  private _selectedPeriodTask = new Task(this, {
    args: () => [this._periodsTask.value, this.state?.selected] as const,
    task: async ([periods, selected]) => {
      const since = selected?.toString();
      return periods?.find(period => period.since === since) ?? this._defaultPeriod();
    },
  });

  // The environments offered by the timeline selector are the ones that reported
  // inside the window the chart is showing, so the selector cannot offer a split
  // that plots nothing.
  private _environmentsTask = new Task(this, {
    args: () => [this._project, this._snapshotId, this._headRefTask.value, this._periodsTask.value] as const,
    task: async ([project, snapshotId, headRef, periods], { signal }) => {
      CHECK(project && snapshotId && headRef);
      const oldest = periods?.at(0);
      const newest = periods?.at(-1);
      if (!oldest || !newest)
        return [];
      const timezone = project.timezone;
      return await api.report.allEnvironments.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        commitOptions: {
          head: headRef.commit.commitId,
          sinceTimestamp: Temporal.PlainDate.from(oldest.since).toZonedDateTime(timezone).epochMilliseconds as FlakinessReport.UnixTimestampMS,
          untilTimestamp: Temporal.PlainDate.from(newest.until).add({ days: 1 }).toZonedDateTime(timezone).epochMilliseconds as FlakinessReport.UnixTimestampMS,
        },
      }, { signal });
    },
  });

  // The tests behind the selected numbers. The table pages and sorts through this provider.
  private _testsProviderTask = new Task(this, {
    args: () => [
      this._project,
      this._snapshotId,
      this._headRefTask.value,
      this._commitRegistry,
      this.state?.period,
      this.state?.classification,
      this._split(),
      // The summary object, not its `since`: refetched periods can keep a `since` but move
      // their `until` or their baseline.
      this._selectedPeriodTask.value,
      this.state?.transitions,
      this.state?.query,
    ] as const,
    task: async ([project, snapshotId, headRef, commitRegistry, period, classification, split, selected, transitions, query]) => {
      const previous = selected && comparedTo(selected);
      if (!selected || !previous)
        return undefined;
      CHECK(project && snapshotId && headRef && commitRegistry && period && classification && split);
      const since = Temporal.PlainDate.from(selected.since);
      // A row's status is the outcome the aggregation picked, so the column header carries its icon.
      const statusColumn = createStatusColumn({ commitRegistry, name: html`<h-box style="gap: var(--sl-spacing-2x-small);">${classificationIcon(classification)} S</h-box>` });
      const history = HISTORY_BUCKETS[period];
      // One column per period, so a row reads "how it behaved before" then "how it behaves now".
      // The history covers both periods, and every bar sits in exactly one of them.
      const historyColumn = (range: { since: Temporal.PlainDate, until: Temporal.PlainDate }) => createTestHistoryColumn<WireTypes.TestStats>({
        name: rangeLabel(range.since, range.until, { year: false }),
        timezone: project.timezone,
        history: testStats => testStats.history,
        commitRegistry,
        range,
        // The popup opens on the clicked bucket. Its calendar keeps its own viewport, which is
        // twelve weeks wide, rather than being squeezed into the compared periods.
        onHistorySelected: (testStats, selection) => void this._historyPopup?.showHistoryBar({
          testStats,
          selection,
          state: {
            timelineSplit: split,
            head: headRef.commit.commitId,
            testId: testStats.test.testId,
            testTimeline: EnvGroup.deserialize(testStats.timeline),
          },
        }),
      });
      const until = Temporal.PlainDate.from(selected.until);
      return {
        columns: [
          historyColumn(previous),
          historyColumn({ since, until }),
          statusColumn,
          testStatsColumns.name,
          testStatsColumns.timeline,
          // The columns here are fixed, so the table has no column settings. Duration shares its
          // setting with the tests page, and hiding it there would hide it here for good.
          { ...testStatsColumns.duration, setting: undefined },
        ],
        defaultSortColumn: statusColumn,
        loader: async (pageOptions, sortOptions, signal) => {
          const result = await api.analytics.listTests.POST({
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            snapshotId,
            head: headRef.commit.commitId,
            timeZoneId: project.timezone,
            timelineSplit: split.serialize(),
            classification,
            current: { since: since.toString(), until: until.toString() },
            baseline: { since: previous.since.toString(), until: previous.until.toString() },
            transitions,
            fql: query,
            history,
            pageOptions,
            sortOptions: sortOptions ?? { axis: 'outcome', direction: 'desc' },
          }, { signal });
          commitRegistry.addCommits(result.commits);
          return result.page;
        },
      } satisfies DataTableProvider<WireTypes.TestStats, WireTypes.TestStatsSortAxis>;
    },
  });

  /**
   * What the page opens on: the newest period that reported tests. The newest period itself is
   * usually still filling up, and an empty one has nothing to show.
   */
  private _defaultPeriod(): WireTypes.DayPeriodSummary|undefined {
    const periods = this._periodsTask.value;
    return periods?.findLast(period => TestOutcomes.sumAllCounts(period.testStats) > 0) ?? periods?.at(-1);
  }

  private _linkify(update: Partial<FeatureProjectAnalyticsState>) {
    if (!this.state)
      return;
    return this.linkifier?.({ ...this.state, ...update });
  }

  // Offer a swatch only for outcomes that actually occur in the window. A selected
  // outcome stays offered once it drops to zero, so moving the window cannot strand a selection.
  private _chartOutcomes(): WireTypes.Outcome[] {
    const periods = this._periodsTask.value ?? [];
    return OUTCOME_ORDER.filter(outcome =>
      this._selectedOutcomes.includes(outcome) || periods.some(period => period.testStats[outcome] > 0));
  }

  render() {
    if (!this._project || !this.state)
      return nothing;

    const period = this.state.period;
    return html`
      <v-box>
        <slot name=header></slot>
        <!-- What the page looks at, the window it shows, then how a test's results count. -->
        <h-box class=controls>
          <head-selector
            .head=${this._headRefTask.value}
            @fk-select=${(event: FKHeadSelectEvent) => handleLink(this._router, this._linkify({
              head: event.head.name,
            }))}
          ></head-selector>
          <timeline-selector multiple
            .envs=${this._environmentsTask.value}
            .split=${this._split()}
            .defaultSplit=${this._defaultSplit.value}
            @fk-select=${(event: CustomEvent<EnvGroup>) => handleLink(this._router, this._linkify({
              timelineSplit: event.detail,
            }))}
          ></timeline-selector>
          <sl-radio-group value=${period}
            @sl-change=${(event: Event) => handleLink(this._router, this._linkify({
              period: (event.target as SlRadioGroup).value as AnalyticsPeriod,
              selected: undefined,
              transitions: undefined,
            }))}
          >
            ${PERIODS.map(value => html`
              <sl-radio-button value=${value}>${windowLabel(value)}</sl-radio-button>
            `)}
          </sl-radio-group>
          ${this._renderNavigation()}
          <x-filler></x-filler>
          <h-box class=outcome-control>
            <h-box class=control-label>
              Result Aggregation
              <fk-help>
                <p>Defines how test results are aggregated inside each period across observed commits.</p>
                ${WireTypes.OUTCOME_CLASSIFICATIONS.map(renderAggregationExample)}
              </fk-help>
            </h-box>
            <sl-radio-group size=small value=${this.state.classification}
              @sl-change=${(event: Event) => handleLink(this._router, this._linkify({
                classification: (event.target as SlRadioGroup).value as WireTypes.OutcomeClassification,
              }))}
            >
              ${WireTypes.OUTCOME_CLASSIFICATIONS.map(value => html`
                <sl-tooltip content=${CLASSIFICATION_LABELS[value]}>
                  <sl-radio-button value=${value}>${classificationIcon(value)}</sl-radio-button>
                </sl-tooltip>
              `)}
            </sl-radio-group>
          </h-box>
        </h-box>

        <div class=chart-header>
          <x-filler></x-filler>
          <outcome-legend
            .outcomes=${this._chartOutcomes()}
            .selected=${this._selectedOutcomes}
            @fk-select=${(event: FKOutcomeSelectEvent) => this._selectedOutcomes = event.outcomes}
          ></outcome-legend>
        </div>

        <div class=chart-box>
          ${this._periodsTask.render({ pending: () => html`<fk-loading-glass></fk-loading-glass>` })}
          <period-chart
            style=${styleMap({ height: '220px' })}
            .periods=${this._periodsTask.value}
            .state=${{
              period,
              stackedOutcomes: this._selectedOutcomes,
              selected: this._selectedPeriodTask.value?.since,
            } satisfies PeriodChartState}
            .linkifier=${(chart: PeriodChartState) => this._linkify({
              // The default period does not need to be in the URL.
              selected: chart.selected && chart.selected !== this._defaultPeriod()?.since ? Temporal.PlainDate.from(chart.selected) : undefined,
              transitions: undefined,
            })}
          ></period-chart>
        </div>

        ${this._renderPeriodDetails()}
      </v-box>
    `;
  }

  private _renderPeriodDetails() {
    const selected = this._selectedPeriodTask.value;
    if (!this.state || !selected)
      return nothing;
    const since = Temporal.PlainDate.from(selected.since);
    const until = Temporal.PlainDate.from(selected.until);
    // Every period in the window has one, since the window is fetched with one more period before it.
    const previous = comparedTo(selected);
    if (!previous)
      return nothing;
    return html`
      <section-separator>${rangeLabel(since, until)}</section-separator>
      <outcome-matrix
        .transitions=${selected.comparedTo?.transitions}
        .selected=${this.state.transitions}
        .beforeLabel=${rangeLabel(previous.since, previous.until, { year: false })}
        .afterLabel=${rangeLabel(since, until, { year: false })}
        .linkifier=${(transitions: WireTypes.TransitionFilter[]|undefined) => this._linkify({ transitions })}
      ></outcome-matrix>
      ${this._renderTests()}
    `;
  }

  private _renderTests() {
    const selected = this._selectedPeriodTask.value;
    if (!selected)
      return nothing;
    // Without a picked cell or header, the list is every test of the period.
    const transitions = this.state?.transitions;
    return html`
      <feature-commit-history-popup></feature-commit-history-popup>
      <div class=tests-header>
        ${!transitions ? nothing : html`
          <sl-tag size=medium removable
            @sl-remove=${() => handleLink(this._router, this._linkify({ transitions: undefined }))}
          >${transitionsLabel(transitions)}</sl-tag>
        `}
        <fql-search q=${this.state?.query ?? ''} @fql-changed=${(event: FqlChangedEvent) => {
          handleLink(this._router, this._linkify({ query: event.query }));
        }}></fql-search>
      </div>
      <data-table
        .provider=${this._testsProviderTask.value}
        .pageNumber=${0}
        .pageSize=${25}
        noDataMessage
      ></data-table>
    `;
  }

  /** The newest period the chart shows. */
  private _renderNavigation() {
    const project = this._project;
    const state = this.state;
    if (!project || !state)
      return nothing;

    const today = Temporal.Now.plainDateISO(project.timezone);
    // Whether the chart ends with the period that is still running.
    const isCurrent = (until: Temporal.PlainDate|undefined) => !until || windowEnd(state.period, until, today).equals(today);
    // Showing the running period is the same as following the latest data.
    const showUntil = (until: Temporal.PlainDate|undefined) => handleLink(this._router, this._linkify({
      until: isCurrent(until) ? undefined : until,
      transitions: undefined,
    }));
    const isLatest = isCurrent(state.until);

    // The window picker, the caption and the picked period read as one phrase: "20 weeks until This week".
    return html`
      <h-box class=period-control>
        <span class=control-label>until</span>
        <period-picker
          .period=${state.period}
          .day=${isLatest ? undefined : state.until}
          .timezone=${project.timezone}
          @fk-select=${(event: FKPeriodSelectEvent) => showUntil(event.day)}
        ></period-picker>
      </h-box>
    `;
  }

  static styles = css`
    :host {
      display: block;
    }

    .controls {
      flex-wrap: wrap;
      align-items: center;
    }

    .chart-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--sl-spacing-x-small);
      margin-top: var(--sl-spacing-medium);
    }

    .control-label {
      gap: var(--sl-spacing-2x-small);
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }

    .period-control, .outcome-control {
      align-items: center;
      gap: var(--sl-spacing-small);
    }

    /* Sized like the button text around it, since it reads as part of the same phrase. */
    .period-control .control-label {
      font-size: var(--sl-font-size-medium);
    }

    .outcome-control sl-radio-button sl-icon {
      font-size: var(--sl-font-size-medium);
    }

    .aggregation-example {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-2x-small);
      padding-top: var(--sl-spacing-x-small);
    }

    .aggregation-title {
      font-weight: var(--sl-font-weight-semibold);

      sl-icon {
        vertical-align: -0.125em;
      }
    }

    .aggregation-flow {
      gap: var(--sl-spacing-x-small);
      color: var(--sl-color-neutral-500);
    }

    .aggregation-bars {
      gap: 4px;
    }

    .aggregation-bar {
      width: 10px;
      height: 20px;
      border-radius: 2px;

      &[winner] {
        outline: 2px solid var(--sl-color-neutral-900);
        outline-offset: 1px;
      }
    }

    .aggregation-outcome {
      gap: var(--sl-spacing-2x-small);
      color: var(--sl-color-neutral-700);
    }

    .chart-box {
      position: relative;
      display: flex;
    }

    section-separator {
      margin-bottom: var(--sl-spacing-medium);
    }

    .tests-header {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-small);
      margin: var(--sl-spacing-x-large) 0 var(--sl-spacing-small);
    }

    /* The selected transition opens the row, and the filter takes the rest of it. */
    fql-search {
      flex: 1;
      min-width: 0;
    }

    sl-tag {
      flex: none;
    }
  `;
}
